import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { notification, Select, Spin } from "antd";
import { NepaliDatePicker } from "nepali-datepicker-reactjs";
import "nepali-datepicker-reactjs/dist/index.css";

import { addBooking } from "../../redux/bookingSlice";
import { convertToEnglishDate } from "../../components/FormatDate";
import { fetchUsers } from "../../redux/userSlice";

const { Option } = Select;

const AddBooking = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Redux state: users from userSlice
  const { users = [], loading: usersLoading = false, error: usersError } = useSelector(
    (state) => state.user || {}
  );

  // DEBUG: Log the entire user state
  const userState = useSelector((state) => state.user);
  console.log("🔍 Full User State:", userState);
  console.log("👥 Users Array:", users);
  console.log("📊 Users Length:", users?.length);

  // Form states
  const [hallName, setHallName] = useState({
    value: "Community Hall",
    available: true,
  });
  const [selectedUser, setSelectedUser] = useState(null);
  const [bookingDate, setBookingDate] = useState("");
  const [startTime, setStartTime] = useState("06:00");
  const [endTime, setEndTime] = useState("21:00");
  const [remarks, setRemarks] = useState("");
  const [errors, setErrors] = useState({});
  const [bookLoading, setBookLoading] = useState(false);

  // Fetch users on mount
  useEffect(() => {
    console.log("🚀 Fetching users...");
    dispatch(fetchUsers({ paginate: false }))
      .unwrap()
      .then((response) => {
        console.log("✅ fetchUsers response:", response);
        console.log("📦 response.data:", response?.data);
        console.log("📦 response.data.data:", response?.data?.data);
      })
      .catch((error) => {
        console.error("❌ fetchUsers error:", error);
      });
  }, [dispatch]);

  const hallOptions = [
    {
      label: "Community Hall",
      value: "Community Hall",
      available: true,
    },
  ];

  const validateForm = () => {
    const newErrors = {};
    if (!hallName?.value) newErrors.hallName = "Please select a hall.";
    if (!selectedUser) newErrors.user_id = "Please select a user.";
    if (!bookingDate) newErrors.bookingDate = "Booking date is required.";
    if (!startTime) newErrors.startTime = "Start time is required.";
    if (!endTime) newErrors.endTime = "End time is required.";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setBookLoading(true);

    const bookingData = {
      user_id: selectedUser.id,
      hall_name: hallName.value,
      booking_date: convertToEnglishDate(bookingDate),
      start_time: startTime,
      end_time: endTime,
      remarks,
    };

    dispatch(addBooking(bookingData))
      .unwrap()
      .then(() => {
        notification.success({
          message: "Success",
          description: "Booking added successfully",
        });
        setBookLoading(false);

        // Reset form
        setHallName({ value: "Community Hall", available: true });
        setSelectedUser(null);
        setBookingDate("");
        setStartTime("06:00");
        setEndTime("21:00");
        setRemarks("");

        navigate("/dashboard/booking");
      })
      .catch((err) => {
        notification.error({
          message: "Error",
          description: err.message || "Failed to add booking",
        });
        setBookLoading(false);
      });
  };

  return (
    <div className="pb-20 mx-6">
      <h3 className="font-bold flex text-[22px]">Add Booking</h3>

      <div className="bg-gray-300 rounded-lg p-4">
        <div className="py-6 bg-white rounded-lg m-3 p-4">
          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            {/* Hall Select */}
            <div>
              <label htmlFor="hall_name" className="font-bold text-md">
                Choose Your Hall
              </label>
              <Select
                id="hall_name"
                value={hallName?.value}
                onChange={(value, option) =>
                  setHallName({ value, available: option.available ?? true })
                }
                className="mt-2 w-full"
                placeholder="Select Hall"
              >
                {hallOptions.map((opt) => (
                  <Option
                    key={opt.value}
                    value={opt.value}
                    available={opt.available}
                    disabled={!opt.available}
                  >
                    {opt.label} {!opt.available && "(Unavailable)"}
                  </Option>
                ))}
              </Select>
              {errors.hallName && (
                <p className="text-red-500 text-sm">{errors.hallName}</p>
              )}
            </div>

            {/* User Select */}
            <div>
              <label htmlFor="user_id" className="font-bold text-md">
                Choose User
              </label>

              {usersLoading ? (
                <div className="flex justify-center mt-2">
                  <Spin size="small" />
                </div>
              ) : usersError ? (
                <p className="text-red-500 text-sm mt-2">
                  Failed to load users: {JSON.stringify(usersError)}
                </p>
              ) : (
                <Select
                  id="user_id"
                  showSearch
                  optionFilterProp="children"
                  placeholder="Search and select user"
                  className="mt-2 w-full"
                  value={selectedUser?.id}
                  onChange={(value, option) => setSelectedUser(option.userObj)}
                  notFoundContent="No users found"
                >
                  {users.map((u) => (
                    <Option key={u.id} value={u.id} userObj={u}>
                      {u.name || u.email || `User ${u.id}`}
                    </Option>
                  ))}
                </Select>
              )}

              {errors.user_id && (
                <p className="text-red-500 text-sm">{errors.user_id}</p>
              )}
            </div>

            {/* Booking Date */}
            <div>
              <label htmlFor="booking_date" className="font-bold text-md">
                Booking Date
              </label>
              <NepaliDatePicker
                id="booking_date"
                className="w-full custom-date-picker mt-2"
                options={{ calenderLocale: "en", valueLocale: "en" }}
                value={bookingDate}
                onChange={setBookingDate}
                required
              />
              {errors.bookingDate && (
                <p className="text-red-500 text-sm">{errors.bookingDate}</p>
              )}
            </div>

            {/* Hidden Time Inputs */}
            <div className="hidden">
              <label htmlFor="start_time" className="font-bold text-md">
                Start Time
              </label>
              <input
                type="time"
                id="start_time"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                className="rounded-md w-full py-2 px-4 border-[2px] border-gray-400 mt-2"
              />
              {errors.startTime && (
                <p className="text-red-500 text-sm">{errors.startTime}</p>
              )}
            </div>

            <div className="hidden">
              <label htmlFor="end_time" className="font-bold text-md">
                End Time
              </label>
              <input
                type="time"
                id="end_time"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
                className="rounded-md w-full py-2 px-4 border-[2px] border-gray-400 mt-2"
              />
              {errors.endTime && (
                <p className="text-red-500 text-sm">{errors.endTime}</p>
              )}
            </div>

            {/* Remarks */}
            <div>
              <label htmlFor="remarks" className="font-bold text-md">
                Remarks
              </label>
              <input
                type="text"
                id="remarks"
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                className="rounded-md w-full py-2 px-4 border-[2px] border-gray-400 mt-2"
              />
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={bookLoading}
              className={`bg-[#403F93] text-white flex px-16 py-3 rounded-lg mt-6 ${
                bookLoading ? "opacity-50 cursor-not-allowed" : ""
              }`}
            >
              {bookLoading ? "Loading..." : "Book"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddBooking;